import axios from 'axios';
import { print } from 'graphql/language/printer';
import moment from 'moment';
import groupBy from 'lodash/groupBy';
import mapValues from 'lodash/mapValues';

import { SYNC_BILL_SERVICE_URL } from '../syncBill/constants';
import axiosInstance from '../../utils/axiosInstance';
import { isLoggedIn, invalidateSession } from '../../utils/auth';
import { assignOutletIdHeader } from '../../utils/selectedOutletId';
import * as api from '../constants';
import { serviceReportingErrorCallback } from '../callbacks';
import {
  // @ts-expect-error Module '"*.gql"' has no exported member 'SalesSummaryQuery'.ts(2305)
  SalesSummaryQuery,
  // @ts-expect-error Module '"*.gql"' has no exported member 'ItemSummaryQuery'.ts(2305)
  ItemSummaryQuery,
  // @ts-expect-error Module '"*.gql"' has no exported member 'TopItemsQuery'.ts(2305)
  TopItemsQuery,
  // @ts-expect-error Module '"*.gql"' has no exported member 'TopCategoriesByQuery'.ts(2305)
  TopCategoriesByQuery,
  // @ts-expect-error Module '"*.gql"' has no exported member 'TopItemsByCategoryQuery'.ts(2305)
  TopItemsByCategoryQuery,
} from './queries.gql';

function fillDailyGrossSalesGap(startDate, endDate, dailyGrossSales) {
  const startMoment = moment(startDate);
  const endMoment = moment(endDate);
  const result: any[] = [];

  const currentMoment = startMoment;
  let index = 0;
  while (currentMoment.isBefore(endMoment, 'day')) {
    if (
      index >= dailyGrossSales.length ||
      currentMoment.isBefore(dailyGrossSales[index].date, 'day')
    ) {
      result.push({
        date: currentMoment.format('YYYY-MM-DD'),
        sumGrossSales: 0,
        numSales: 0,
      });
    } else {
      result.push(dailyGrossSales[index]);
      index++;
    }
    currentMoment.add(1, 'day');
  }

  return result;
}

// The so called "weekly" is actually the sales of each day of week in the date
// range. Like the total sales of all Sundays within the period.
// The range here is Sunday to Saturday.
function fillWeeklyGrossSalesGap(entries) {
  const dayFormat = 'ddd';
  const result = new Array(7).fill(0).map((_, index) => ({
    dayOfWeek: moment().day(index).format(dayFormat),
    sumGrossSales: 0,
    numSales: 0,
  }));

  for (const entry of entries) {
    const dayMoment = moment().day(entry.dayOfWeek);
    result[dayMoment.day()] = {
      ...entry,
      dayOfWeek: dayMoment.format(dayFormat),
    };
  }

  return result;
}

function fillHourlyGrossSalesGap(entries) {
  const result = new Array(24).fill(0).map((_, index) => ({
    hour: index,
    sumGrossSales: 0,
    numSales: 0,
  }));

  for (const entry of entries) {
    result[entry.hour] = {
      ...entry,
      hour: parseInt(entry.hour, 10),
    };
  }

  return result;
}

function formatTopItemByCategories(entries) {
  const groups = groupBy(entries, 'categoryName');

  return mapValues(groups, (items) =>
    items.map((item) => ({
      name: item.itemName,
      count: item.volume,
      revenue: item.revenue,
    }))
  );
}

function formatTopCategories(entries) {
  const groups = groupBy(entries, 'categoryName');
  return mapValues(groups, (items) => items[0]);
}

export function loggedIn() {
  const hasValidSession = isLoggedIn();

  if (!hasValidSession) {
    invalidateSession();
  }
  return hasValidSession;
}

export function dashboard(query) {
  if (!loggedIn()) return Promise.reject(new Error('Not logged in'));

  const headers = { params: query, withCredentials: true };
  const finalHeaders = assignOutletIdHeader(headers);

  return axios
    .get(api.GET_DASHBOARD, finalHeaders)
    .catch(serviceReportingErrorCallback);
}
/* --- async dashboard --- */
export function getTableDashboard(params) {
  const headers = { params, withCredentials: true };
  const finalHeaders = assignOutletIdHeader(headers);

  return axios
    .get(`${SYNC_BILL_SERVICE_URL}/bills/stats`, finalHeaders)
    .catch(serviceReportingErrorCallback);
}

/**
 * @param {Object} params
 * @param {string} params.startDate
 * @param {string} params.endDate
 * @param {number[]} params.outletIds
 */
export function getDashboardSalesSummary(params) {
  return axiosInstance
    .post('/mkService/reporting-revamp/graphql', {
      query: print(SalesSummaryQuery),
      variables: params,
    })
    .then((response) => {
      const {
        grossProfitReport,
        salesSummaryReport,
        dailyGrossSales,
        hourlyGrossSales,
        weeklyGrossSales,
      } = response.data.data;
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { grossProfit: gross_profit } = grossProfitReport;
      const {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        grossSales: gross_sales,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        netSales: net_sales,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        numberOfTransactions: total_transactions,
      } = salesSummaryReport;

      const { startDate, endDate } = params;

      return {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        gross_profit,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        gross_sales,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        net_sales,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        total_transactions,
        weeklyGrossSales: fillWeeklyGrossSalesGap(weeklyGrossSales),
        hourlyGrossSales: fillHourlyGrossSalesGap(hourlyGrossSales),
        dailyGrossSales: fillDailyGrossSalesGap(
          startDate,
          endDate,
          dailyGrossSales
        ),
      };
    });
}

/**
 * @param {Object} params
 * @param {string} params.startDate
 * @param {string} params.endDate
 * @param {number[]} params.outletIds
 */
export function getDashboardItemSummary(params) {
  return axiosInstance
    .post('/mkService/reporting-revamp/graphql', {
      query: print(ItemSummaryQuery),
      variables: params,
    })
    .then((response) => {
      const {
        topItems,
        topItemsByCategoryReport,
        topCategoriesBySalesVolumeReport,
        topCategoriesBySalesRevenueReport,
      } = response.data.data;

      return {
        categorySalesAmountByRevenue: formatTopCategories(
          topCategoriesBySalesRevenueReport
        ),
        categorySalesAmountByVolume: formatTopCategories(
          topCategoriesBySalesVolumeReport
        ),
        topItemsByCategory: formatTopItemByCategories(topItemsByCategoryReport),
        // eslint-disable-next-line @typescript-eslint/naming-convention
        top_items: topItems,
      };
    });
}

/**
 * @param {Object} params
 * @param {string} params.startDate
 * @param {string} params.endDate
 * @param {number[]} params.outletIds
 */
export function getDashboardTopItems(params) {
  return axiosInstance
    .post('/mkService/reporting-revamp/graphql', {
      query: print(TopItemsQuery),
      variables: params,
    })
    .then((response) => {
      const { topItems } = response.data.data;

      return {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        top_items: topItems,
      };
    });
}

/**
 * @param {Object} params
 * @param {string} params.startDate
 * @param {string} params.endDate
 * @param {number[]} params.outletIds
 */
export function getDashboardTopItemsByCategory(params) {
  return axiosInstance
    .post('/mkService/reporting-revamp/graphql', {
      query: print(TopItemsByCategoryQuery),
      variables: params,
    })
    .then((response) => {
      const { topItemsByCategoryReport } = response.data.data;

      return {
        topItemsByCategory: formatTopItemByCategories(topItemsByCategoryReport),
      };
    });
}

/**
 * @param {Object} params
 * @param {string} params.startDate
 * @param {string} params.endDate
 * @param {number[]} params.outletIds
 */
export function getDashboardTopCategoriesBy(params) {
  return axiosInstance
    .post('/mkService/reporting-revamp/graphql', {
      query: print(TopCategoriesByQuery),
      variables: params,
    })
    .then((response) => {
      const {
        topCategoriesBySalesVolumeReport,
        topCategoriesBySalesRevenueReport,
      } = response.data.data;

      return {
        categorySalesAmountByRevenue: formatTopCategories(
          topCategoriesBySalesRevenueReport
        ),
        categorySalesAmountByVolume: formatTopCategories(
          topCategoriesBySalesVolumeReport
        ),
      };
    });
}

export function dashboardSummaryPost(dataPost, outletIds) {
  return axiosInstance
    .post('/mkService/reporting/v1/dashboard/sales_summary', dataPost, {
      headers: { OUTLET_IDS: outletIds },
      withCredentials: false,
    })
    .then((res) => res.data)
    .catch(serviceReportingErrorCallback);
}

export function dashboardItemSummaryPost(dataPost, outletIds) {
  return axiosInstance
    .post('/mkService/reporting/v1/dashboard/item_summary', dataPost, {
      headers: { OUTLET_IDS: outletIds },
      withCredentials: false,
    })
    .then((res) => res.data)
    .catch(serviceReportingErrorCallback);
}

export function getDataAsync(urls3) {
  if (!loggedIn()) return Promise.reject(new Error('Not logged in'));
  return (
    axios
      // eslint-disable-next-line @typescript-eslint/naming-convention
      .get(urls3, { headers: { 'Content-Encoding': 'gzip' } })
      .catch(serviceReportingErrorCallback)
  );
}
